import * as PIXI from 'pixi.js';
import type { VideoFramesProvider, VideoFramesProviderFactory } from '../video/VideoFramesProvider';
import { WebpResource } from './WebpResource';
import { VideoFramesProviderResource } from './VideoFramesProviderResource';
import { observesPixi } from '../reactivity';

type UnderlyingPixiResource = PIXI.ImageResource | PIXI.SVGResource;
type UnderlyingResource = UnderlyingPixiResource | WebpResource | VideoFramesProviderResource;

export type MediaResourceKind = 'video' | 'image' | 'svg' | 'webp';

/**
 * The pixels a media resource currently exposes. Anything here can be passed to
 * `drawImage`/`texImage2D`.
 */
export type MediaFrameSource = CanvasImageSource;

export type MediaResourceCreateParams = {
    sourceUrl: string | null;
    sourceMimeType: 'video/mp4' | null;
    isVideo: boolean;
    isSvg: boolean;
    sourceDuration: number | null;
    createVideoFramesProvider: VideoFramesProviderFactory;
};

/**
 * Encapsulates a single media asset resource behind one interface, hiding which concrete
 * PIXI resource (or {@link WebpResource}) backs it. This lets MediaAsset operate on media
 * without branching on the resource/asset type in every method.
 */
export class MediaResource {
    @observesPixi(['_width', '_height', 'source'], { only: PIXI.BaseImageResource })
    accessor #resource: UnderlyingResource;
    #kind: MediaResourceKind;

    constructor(resource: UnderlyingResource, kind: MediaResourceKind) {
        this.#resource = resource;
        this.#kind = kind;
    }

    /**
     * Create the appropriate resource for the given source. The selection order matches the
     * legacy logic: animated WebP is detected first (even for video source data), then video,
     * then SVG, then a plain image.
     */
    static create({
        sourceUrl,
        sourceMimeType,
        isVideo,
        isSvg,
        sourceDuration,
        createVideoFramesProvider,
    }: MediaResourceCreateParams): MediaResource {
        if (WebpResource.isWebp(sourceUrl)) {
            if (!sourceUrl) {
                throw new Error('Cannot create webp resource without a source URL.');
            }
            return new MediaResource(new WebpResource(sourceUrl, sourceDuration), 'webp');
        }
        if (isVideo) {
            if (!sourceUrl) {
                throw new Error('Cannot create video resource without a source URL.');
            }
            if (!sourceMimeType) {
                throw new Error('Cannot create video resource without a source MIME type.');
            }
            const videoFramesProvider = createVideoFramesProvider({
                sourceUrl,
                mimeType: sourceMimeType,
            });
            videoFramesProvider.debugName = 'original';
            return new MediaResource(
                new VideoFramesProviderResource(videoFramesProvider, {
                    autoLoad: false,
                    autoPlay: false,
                }),
                'video',
            );
        }
        if (!sourceUrl) {
            throw new Error('Cannot create image resource without a source URL.');
        }
        if (isSvg) {
            return new MediaResource(new PIXI.SVGResource(sourceUrl, {}), 'svg');
        }
        return new MediaResource(new PIXI.ImageResource(sourceUrl, { autoLoad: false }), 'image');
    }

    /**
     * The underlying resource. Prefer the wrapper's own methods; use this only where the
     * concrete resource object is genuinely required.
     */
    get raw(): UnderlyingResource {
        return this.#resource;
    }

    get kind(): MediaResourceKind {
        return this.#kind;
    }

    get isVideo(): boolean {
        return this.#kind === 'video';
    }

    get isWebp(): boolean {
        return this.#kind === 'webp';
    }

    get isImage(): boolean {
        return this.#kind === 'image';
    }

    get isSvg(): boolean {
        return this.#kind === 'svg';
    }

    get source(): MediaFrameSource | null {
        return (this.#resource.source ?? null) as MediaFrameSource | null;
    }

    get width(): number {
        return this.#resource.width;
    }

    get height(): number {
        return this.#resource.height;
    }

    get valid(): boolean {
        return this.#resource.valid;
    }

    get destroyed(): boolean {
        return this.#resource.destroyed;
    }

    /**
     * Return the underlying resource's load promise directly rather than wrapping it in an
     * `async` method. An `async` wrapper adds an extra microtask before the caller's `.then`
     * runs, which shifts the resulting redraw into a later build pass and reintroduces the
     * watermark create/destroy loop (see watermark_tests "does not create a loop..."). Keep this
     * non-async to preserve the original rebuild timing.
     */
    // eslint-disable-next-line @typescript-eslint/promise-function-async
    load(): Promise<unknown> {
        return this.#resource.load();
    }

    destroy(): void {
        this.#resource.destroy();
    }

    /**
     * The backing {@link VideoFramesProvider} when this resource is video-backed, otherwise null.
     * WebP resources are never considered video-backed even though they animate.
     */
    get videoFramesProvider(): VideoFramesProvider | null {
        if (!(this.#resource instanceof VideoFramesProviderResource)) {
            return null;
        }
        return this.#resource.provider;
    }

    /**
     * Swap the backing video frames provider. Used to switch the active playback region's video.
     */
    set videoFramesProvider(videoFramesProvider: VideoFramesProvider) {
        if (!(this.#resource instanceof VideoFramesProviderResource)) {
            throw new Error('Cannot set a video frames provider on a non-video resource.');
        }
        this.#resource.provider = videoFramesProvider;
    }

    /**
     * Move the current frame to the given time. Only meaningful for WebP; a no-op otherwise
     * because video/image resources are seeked through their DOM element.
     */
    seek(time: number): void {
        if (this.#resource instanceof WebpResource) {
            this.#resource.seek(time);
        }
    }

    /**
     * Buffer the frame(s) needed for the given time. Only meaningful for WebP; a no-op
     * otherwise because video buffering is driven through the DOM element.
     */
    async buffer(time: number): Promise<void> {
        if (this.#resource instanceof WebpResource) {
            await this.#resource.buffer(time);
        }
    }
}
